import React, { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import StatusPill from '../components/StatusPill';

const EVIDENCE_LABELS = {
  roles: 'Tus roles FORGE',
  completed_resource_ids: 'Recursos completados',
  best_scores: 'Resultados de evaluaciones',
  spec_status_counts: 'Actividad en especificaciones',
  task_status_counts: 'Actividad en tareas',
  tool_call_counts: 'Uso agregado de herramientas',
};

function requestJson(url, options = {}) {
  return fetch(url, {
    credentials: 'include',
    ...options,
    headers: options.body ? { 'Content-Type': 'application/json', ...options.headers } : options.headers,
  }).then(async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.detail || 'No se pudo completar la operación.');
    return data;
  });
}

function CertificateCard({ certificate }) {
  if (!certificate) return null;
  return (
    <section className="learning-certificate" aria-label="Certificación FORGE">
      <div className="learning-certificate-mark">FORGE</div>
      <div>
        <div className="forge-section-eyebrow">Certificación interna</div>
        <h2>Certificación FORGE completada</h2>
        <p>
          Se certifica que <strong>@{certificate.username}</strong> completó la versión{' '}
          {certificate.curriculum_version} de la ruta completa: formación, taller y examen final.
        </p>
        <small>
          Emitido {new Date(certificate.issued_at).toLocaleDateString('es-MX')} · Código{' '}
          {certificate.verification_code}
        </small>
        <Link className="learning-certificate-link" to={`/certificates/verify/${certificate.verification_code}`}>
          Abrir verificación pública
        </Link>
      </div>
      <button type="button" className="forge-button learning-no-print" onClick={() => window.print()}>
        Imprimir credencial
      </button>
    </section>
  );
}

const JOURNEY_LABELS = {
  course: 'Curso FORGE',
  roles: 'Formación por rol',
  workshop: 'Taller práctico',
  exam: 'Examen final',
  certification: 'Certificación',
};

function ContentBlocks({ blocks = [] }) {
  return (
    <div className="learning-content-blocks">
      {blocks.map((block) => {
        if (block.type === 'image') {
          return <figure key={block.id}><img src={block.url} alt={block.alt} /><figcaption><strong>{block.title}</strong>{block.body && <span>{block.body}</span>}</figcaption></figure>;
        }
        if (block.type === 'video') {
          return (
            <article className="learning-media-block" key={block.id}>
              <h4>{block.title}</h4>
              <p>{block.body}</p>
              <video controls preload="metadata"><source src={block.url} /></video>
              <details><summary>Transcripción</summary><p>{block.transcript}</p></details>
            </article>
          );
        }
        if (['link', 'download'].includes(block.type)) {
          return <a className="learning-link-block" href={block.url} target="_blank" rel="noreferrer" key={block.id}><strong>{block.title}</strong><span>{block.body || 'Abrir recurso'} ↗</span></a>;
        }
        if (block.type === 'list') {
          const items = block.items || (block.body || '').split('\n').filter(Boolean);
          return <article key={block.id}><h4>{block.title}</h4><ul>{items.map((item) => <li key={item}>{item}</li>)}</ul></article>;
        }
        return <article className={`learning-copy-block ${block.type || 'text'}`} key={block.id}><h4>{block.title}</h4>{block.body && <p>{block.body}</p>}</article>;
      })}
    </div>
  );
}

function RoleTrack({ track, progress, locked, busy, onSubmit }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const roleProgress = progress.role_progress?.[track.role] || {};
  const submit = async (event) => {
    event.preventDefault();
    try {
      setResult(await onSubmit(track.role, answers));
    } catch {
      // The page-level alert contains the API error.
    }
  };
  return (
    <article className={`learning-role-track ${roleProgress.complete ? 'complete' : ''} ${locked ? 'locked' : ''}`}>
      <div className="learning-role-track-head">
        <div><span>{track.role}</span><h3>{track.title}</h3><p>{track.purpose}</p></div>
        <StatusPill color={roleProgress.complete ? 'green' : locked ? 'amber' : 'blue'}>{roleProgress.complete ? 'Completa' : locked ? 'Bloqueada' : 'Pendiente'}</StatusPill>
      </div>
      <ContentBlocks blocks={track.blocks} />
      <div className="learning-role-facts">
        <div><strong>Responsabilidades</strong><ul>{track.responsibilities.map((item) => <li key={item}>{item}</li>)}</ul><strong>Autoridad y decisiones permitidas</strong><p>{track.authority}</p></div>
        <div><strong>Evidencia esperada</strong><p>{track.evidence}</p><strong>Cuándo escalar</strong><p>{track.escalation}</p></div>
      </div>
      <form className="learning-assessment" onSubmit={submit} aria-disabled={locked}>
        <div className="learning-assessment-head"><div><div className="forge-section-eyebrow">Validación del rol</div><h3>Demuestra tu criterio</h3></div>{roleProgress.best_score > 0 && <StatusPill color={roleProgress.complete ? 'green' : 'amber'}>Mejor: {roleProgress.best_score}%</StatusPill>}</div>
        {track.assessment.map((question) => <fieldset key={question.id} disabled={locked}>{<legend>{question.prompt}</legend>}{question.options.map((option) => <label key={option.id}><input type="radio" name={question.id} checked={answers[question.id] === option.id} onChange={() => setAnswers((prev) => ({ ...prev, [question.id]: option.id }))} /><span>{option.text}</span></label>)}</fieldset>)}
        {result && <div className={`learning-feedback ${result.passed ? 'success' : 'retry'}`}><strong>{result.passed ? `Aprobado · ${result.score}%` : `Resultado · ${result.score}%`}</strong>{result.feedback.map((item) => <p key={item.question_id}>{item.explanation}</p>)}</div>}
        <button type="submit" className="forge-button primary" disabled={locked || busy || track.assessment.some((question) => !answers[question.id])}>Evaluar responsabilidades</button>
      </form>
    </article>
  );
}

function LearningUnit({ unit, index, roles, progress, onToggleResource, onSubmitAssessment, busy }) {
  const [open, setOpen] = useState(index === 0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const unitProgress = progress?.unit_progress?.[unit.id] || {};
  const roleGuidance = roles
    .map((role) => ({ role, text: unit.role_guidance?.[role] }))
    .filter((item) => item.text);

  const submit = async (event) => {
    event.preventDefault();
    try {
      const next = await onSubmitAssessment(unit.id, answers);
      setResult(next);
    } catch {
      // The parent renders the actionable API error.
    }
  };

  return (
    <article className={`learning-unit ${unitProgress.complete ? 'complete' : ''}`}>
      <button type="button" className="learning-unit-head" onClick={() => setOpen((value) => !value)}>
        <span className="learning-unit-number">{String(index + 1).padStart(2, '0')}</span>
        <span className="learning-unit-title">
          <strong>{unit.title}</strong>
          <small>{unit.objective}</small>
        </span>
        <StatusPill color={unitProgress.complete ? 'green' : unitProgress.best_score ? 'amber' : 'blue'}>
          {unitProgress.complete ? 'Completa' : unitProgress.best_score ? `${unitProgress.best_score}%` : 'Pendiente'}
        </StatusPill>
        <span className="learning-unit-chevron" aria-hidden="true">{open ? '−' : '+'}</span>
      </button>

      {open && (
        <div className="learning-unit-body">
          <ContentBlocks blocks={unit.blocks} />
          <div className="learning-unit-copy">
            <div>
              <h3>Ideas clave</h3>
              <div className="learning-concepts">
                {unit.concepts.map((concept) => <span key={concept}>{concept}</span>)}
              </div>
            </div>
            {(unit.role_guidance?.common || roleGuidance.length > 0) && (
              <div className="learning-role-note">
                <span>Para tus roles · {roles.join(', ')}</span>
                {unit.role_guidance?.common && <p>{unit.role_guidance.common}</p>}
                {roleGuidance.map((item) => <p key={item.role}><strong>{item.role}:</strong> {item.text}</p>)}
              </div>
            )}
          </div>

          <div className="learning-resources">
            <h3>Recursos</h3>
            {unit.resources.map((resource) => {
              const checked = progress.completed_resource_ids.includes(resource.id);
              return (
                <div className="learning-resource" key={resource.id}>
                  <div>
                    <span className="forge-kbd">{resource.type || 'recurso'}</span>
                    <a href={resource.url} target="_blank" rel="noreferrer">{resource.title} ↗</a>
                  </div>
                  <label>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={busy}
                      onChange={(event) => onToggleResource(resource.id, event.target.checked)}
                    />
                    {checked ? 'Completado' : 'Marcar completado'}
                  </label>
                </div>
              );
            })}
          </div>

          <form className="learning-assessment" onSubmit={submit}>
            <div className="learning-assessment-head">
              <div>
                <div className="forge-section-eyebrow">Evaluación</div>
                <h3>Comprueba tu aprendizaje</h3>
              </div>
              {unitProgress.best_score > 0 && <StatusPill color={unitProgress.assessment_passed ? 'green' : 'amber'}>Mejor: {unitProgress.best_score}%</StatusPill>}
            </div>
            {unit.assessment.map((question) => (
              <fieldset key={question.id}>
                <legend>{question.prompt}</legend>
                {question.options.map((option) => (
                  <label key={option.id}>
                    <input
                      type="radio"
                      name={question.id}
                      value={option.id}
                      checked={answers[question.id] === option.id}
                      onChange={() => setAnswers((prev) => ({ ...prev, [question.id]: option.id }))}
                    />
                    <span>{option.text}</span>
                  </label>
                ))}
              </fieldset>
            ))}
            {result && (
              <div className={`learning-feedback ${result.passed ? 'success' : 'retry'}`}>
                <strong>{result.passed ? `Aprobado · ${result.score}%` : `Resultado · ${result.score}%`}</strong>
                {result.feedback.map((item) => <p key={item.question_id}>{item.explanation}</p>)}
              </div>
            )}
            <button
              type="submit"
              className="forge-button primary"
              disabled={busy || unit.assessment.some((question) => !answers[question.id])}
            >
              Evaluar respuestas
            </button>
          </form>
        </div>
      )}
    </article>
  );
}

function Learn() {
  const { apiUrl } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setData(await requestJson(`${apiUrl}/api/learning`));
      setMessage(null);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [apiUrl]);

  const completedUnits = useMemo(
    () => Object.values(data?.progress?.unit_progress || {}).filter((unit) => unit.complete).length,
    [data]
  );

  const updateFromResponse = (response) => {
    setData((prev) => ({
      ...prev,
      progress: response.progress || prev.progress,
      formation_credential: response.formation_credential || prev.formation_credential,
      certificate: response.certificate || prev.certificate,
    }));
  };

  const submitRoleAssessment = async (role, answers) => {
    setBusy(true);
    try {
      const response = await requestJson(`${apiUrl}/api/learning/roles/${role}/assessment`, {
        method: 'POST', body: JSON.stringify({ answers }),
      });
      updateFromResponse(response);
      return response.result;
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
      throw error;
    } finally {
      setBusy(false);
    }
  };

  const toggleResource = async (resourceId, completed) => {
    setBusy(true);
    try {
      const response = await requestJson(`${apiUrl}/api/learning/resources/${resourceId}`, {
        method: 'PUT', body: JSON.stringify({ completed }),
      });
      updateFromResponse(response);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setBusy(false);
    }
  };

  const submitAssessment = async (unitId, answers) => {
    setBusy(true);
    try {
      const response = await requestJson(`${apiUrl}/api/learning/units/${unitId}/assessment`, {
        method: 'POST', body: JSON.stringify({ answers }),
      });
      updateFromResponse(response);
      return response.result;
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
      throw error;
    } finally {
      setBusy(false);
    }
  };

  const refreshRecommendation = async () => {
    setBusy(true);
    try {
      const recommendation = await requestJson(`${apiUrl}/api/learning/recommendations/refresh`, { method: 'POST' });
      setData((prev) => ({ ...prev, recommendation }));
      setMessage({ type: 'success', text: 'Recomendación actualizada con evidencia de uso.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="forge-page"><div className="forge-panel">Cargando ruta de aprendizaje…</div></div>;
  if (!data) return <div className="forge-page"><div className="forge-inline-alert">{message?.text || 'No se pudo cargar Aprender.'}</div></div>;

  const { curriculum, progress, recommendation, certificate, historical_certificates: historicalCertificates = [], formation_credential: formationCredential, roles, journey } = data;
  const assignedRoleTracks = (curriculum.role_tracks || []).filter((track) => roles.includes(track.role));
  return (
    <div className="forge-page learning-page">
      <div className="forge-page-header">
        <div>
          <div className="forge-section-eyebrow">Módulo 01 · Aprender · v{curriculum.version}</div>
          <h1 className="forge-title">{curriculum.title}</h1>
          <p className="forge-lead">{curriculum.description}</p>
        </div>
        <StatusPill color={progress.completion_pct === 100 ? 'green' : 'blue'}>{progress.completion_pct}% completado</StatusPill>
      </div>

      {message && <div className={`admin-message ${message.type}`}>{message.text}</div>}

      <ol className="learning-journey" aria-label="Ruta de certificación FORGE">
        {Object.entries(JOURNEY_LABELS).map(([key, label], index) => (
          <li className={journey?.[key] || 'locked'} key={key}>
            <span>{index + 1}</span><div><strong>{label}</strong><small>{journey?.[key] === 'complete' ? 'Completado' : journey?.[key] === 'available' ? 'Disponible' : journey?.[key] === 'in_progress' ? 'En progreso' : 'Bloqueado'}</small></div>
          </li>
        ))}
      </ol>

      <section className="learning-overview">
        <div className="learning-progress-ring" style={{ '--learning-progress': `${progress.completion_pct * 3.6}deg` }}>
          <span>{progress.completion_pct}%</span>
        </div>
        <div>
          <div className="forge-section-eyebrow">Tu avance personal</div>
          <h2>{completedUnits} de {curriculum.units.length} unidades completas</h2>
          <p>Completa los recursos obligatorios y obtén al menos 80% en cada evaluación.</p>
        </div>
        <div className="learning-overview-stat"><span>Roles</span><strong>{roles.join(', ')}</strong></div>
        <div className="learning-overview-stat"><span>Evaluaciones</span><strong>{progress.passed_unit_ids.length}/{curriculum.units.length}</strong></div>
      </section>

      <div className="learning-layout">
        <section className="learning-roadmap">
          <div className="learning-section-head">
            <div><div className="forge-section-eyebrow">Roadmap común</div><h2>Ruta FORGE</h2></div>
            <span>Avanza a tu ritmo</span>
          </div>
          {curriculum.units.map((unit, index) => (
            <LearningUnit
              key={unit.id}
              unit={unit}
              index={index}
              roles={roles}
              progress={progress}
              onToggleResource={toggleResource}
              onSubmitAssessment={submitAssessment}
              busy={busy}
            />
          ))}
        </section>

        <aside className="learning-aside">
          <section className="forge-panel learning-recommendation">
            <div className="forge-section-eyebrow">Siguiente mejor paso</div>
            <h2>Recomendación con IA</h2>
            {recommendation?.recommendations?.length ? recommendation.recommendations.map((item) => (
              <div className="learning-reco-item" key={item.unit_id}>
                <strong>{curriculum.units.find((unit) => unit.id === item.unit_id)?.title || item.unit_id}</strong>
                <p>{item.reason}</p>
                {item.evidence_signals?.length > 0 && (
                  <div className="learning-reco-evidence" aria-label="Evidencia utilizada">
                    <span>Evidencia utilizada</span>
                    <ul>
                      {item.evidence_signals.map((signal) => (
                        <li key={signal}>{EVIDENCE_LABELS[signal] || signal}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )) : <p>Genera una recomendación usando únicamente señales agregadas de tu actividad FORGE.</p>}
            {recommendation?.stale && <StatusPill color="amber">Desactualizada</StatusPill>}
            <button type="button" className="forge-button" disabled={busy} onClick={refreshRecommendation}>
              {busy ? 'Procesando…' : 'Actualizar recomendación'}
            </button>
          </section>

          <section className="forge-panel learning-axioms">
            <div className="forge-section-eyebrow">Modelo mental</div>
            <h2>Los tres axiomas</h2>
            <ol>
              <li>La intención es la fuente de verdad.</li>
              <li>La autoridad nunca está en el prompt.</li>
              <li>Lo que no deja rastro, no ocurrió.</li>
            </ol>
          </section>
        </aside>
      </div>

      <section className="learning-role-section">
        <div className="learning-section-head">
          <div><div className="forge-section-eyebrow">Etapa 02 · Formación por rol</div><h2>Decide y escala desde tu responsabilidad</h2><p>La autoridad no está en el prompt. Cada rol tiene decisiones, evidencia y límites distintos.</p></div>
          <StatusPill color={progress.roles_complete ? 'green' : progress.course_complete ? 'blue' : 'amber'}>{progress.role_completion_pct}% roles</StatusPill>
        </div>
        {!progress.course_complete && <div className="forge-inline-alert">Completa el curso común para desbloquear las validaciones por rol.</div>}
        <div className="learning-role-grid">
          {assignedRoleTracks.map((track) => <RoleTrack key={track.role} track={track} progress={progress} locked={!progress.course_complete} busy={busy} onSubmit={submitRoleAssessment} />)}
        </div>
      </section>

      {formationCredential && (
        <section className="forge-panel learning-formation-card">
          <div><div className="forge-section-eyebrow">Constancia de formación</div><h2>Teoría y responsabilidades completadas</h2><p>Versión {formationCredential.curriculum_version} · {formationCredential.roles.join(', ')}</p></div>
          <StatusPill color="green">Lista para taller</StatusPill>
        </section>
      )}

      <CertificateCard certificate={certificate} />
      {historicalCertificates.length > 0 && (
        <section className="forge-panel learning-history" aria-label="Certificaciones históricas">
          <div className="forge-section-eyebrow">Historial verificable</div>
          <h2>Certificaciones anteriores</h2>
          <p>Estas credenciales conservan la versión y los roles con los que fueron emitidas. Completa los requisitos pendientes para recuperar el cumplimiento vigente.</p>
          {historicalCertificates.map((item) => (
            <article key={item.verification_code}>
              <div><strong>Versión {item.curriculum_version}</strong><span>{item.roles.join(', ') || 'Roles históricos'}</span></div>
              <Link to={`/certificates/verify/${item.verification_code}`}>Verificar credencial</Link>
            </article>
          ))}
        </section>
      )}
      <section className="forge-panel learning-readiness-cta">
        <div><div className="forge-section-eyebrow">Etapas 03 y 04</div><h2>Taller práctico y examen final</h2><p>{progress.formation_complete ? 'Tu formación está completa. Continúa con la práctica guiada.' : 'Completa curso y roles para desbloquear la práctica.'}</p></div>
        <Link className={`forge-button primary ${progress.formation_complete ? '' : 'disabled'}`} aria-disabled={!progress.formation_complete} to={progress.formation_complete ? '/readiness' : '#'}>Abrir preparación</Link>
      </section>
    </div>
  );
}

export default Learn;
